import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { BtnLoader } from "src/Loader";
import { useAuth } from "src/contexts/auth-provider";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { useRestaurantFromParams } from "src/utils/useRestaurantFromParams";
import { financeUrl, type FinancePayout } from "./financeApi";

export function FinancePayoutsPage() {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const { redirectTo } = useRestaurantFromParams();
  const [payouts, setPayouts] = useState<FinancePayout[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storeCorrelationId = hasRestaurant?.correlationId;

  useEffect(() => {
    if (!storeCorrelationId) return;
    setLoading(true);
    setError(null);
    axBe
      .get<FinancePayout[]>(financeUrl("payouts", storeCorrelationId))
      .then((response) => setPayouts(response.data ?? []))
      .catch(() => setError("Unable to load payouts."))
      .finally(() => setLoading(false));
  }, [axBe, storeCorrelationId]);

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return (
    <ReportPageLayout hideTitle error={error ?? undefined}>
      <div className="w-full overflow-hidden rounded-xl border border-[#F1F1F2] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.04)]">
        {loading ? (
          <div className="flex min-h-[220px] items-center justify-center"><BtnLoader /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-[#f8fafc] text-xs font-semibold uppercase text-[#475569]">
                <tr>
                  <th className="px-4 py-3">Deposit Date</th>
                  <th className="px-4 py-3">Gross</th>
                  <th className="px-4 py-3">Fees</th>
                  <th className="px-4 py-3">Net</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Settlement ID</th>
                </tr>
              </thead>
              <tbody>
                {payouts.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6b7280]">No payouts yet.</td></tr>
                ) : payouts.map((payout) => (
                  <tr key={payout.settlementId ?? payout.depositDate} className="border-t border-[#e5e7eb]">
                    <td className="px-4 py-3">{payout.depositDate ? new Date(payout.depositDate).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">{formatCentsToDollarsWithDollarSign(payout.grossAmountCents ?? 0)}</td>
                    <td className="px-4 py-3">{formatCentsToDollarsWithDollarSign(payout.feeAmountCents ?? 0)}</td>
                    <td className="px-4 py-3 font-semibold">{formatCentsToDollarsWithDollarSign(payout.netAmountCents ?? 0)}</td>
                    <td className="px-4 py-3">{payout.status ?? "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{payout.settlementId ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReportPageLayout>
  );
}
